import React, { useMemo } from 'react';

import {
  AccessRequestStatusContext,
  MentionEntityContext,
  useAccessRequestStatus,
} from 'src/components/ai/chat/chat-hooks/useMentionAccessContext';
import { useMentionEntitiesQuery } from 'src/components/ai/chat/chat-hooks/useMentionEntitiesQuery';

import { extractMentionEntityIdsFromActivityItems } from './activity-comment-mentions';
import type { ActivityItemFragment } from './activity-types';

interface ActivityMentionContextProviderProps {
  children: React.ReactNode;
  items: ActivityItemFragment[];
}

export function ActivityMentionContextProvider({
  children,
  items,
}: ActivityMentionContextProviderProps): JSX.Element {
  const mentionEntityIds = useMemo(() => extractMentionEntityIdsFromActivityItems(items), [items]);
  const { inaccessibleDocumentIds, mentionEntityContextValue } =
    useMentionEntitiesQuery(mentionEntityIds);
  const {
    refetch: refetchAccessRequestStatus,
    statusMap: accessRequestStatusMap,
    updateStatus,
  } = useAccessRequestStatus(inaccessibleDocumentIds);
  const accessRequestStatusContextValue = useMemo(
    () => ({
      refetch: refetchAccessRequestStatus,
      statusMap: accessRequestStatusMap,
      updateStatus,
    }),
    [accessRequestStatusMap, refetchAccessRequestStatus, updateStatus]
  );

  return (
    <MentionEntityContext.Provider value={mentionEntityContextValue}>
      <AccessRequestStatusContext.Provider value={accessRequestStatusContextValue}>
        {children}
      </AccessRequestStatusContext.Provider>
    </MentionEntityContext.Provider>
  );
}
